import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import searchIcon from '../assets/search.png';
import filter from '../assets/filter-svgrepo-com.svg';
import download from '../assets/download-minimalistic-svgrepo-com.svg';
import add from '../assets/add-plus-svgrepo-com.svg';
import CustomDarkCheckbox from '../components/CustomDarkCheckbox';
import arrow from '../assets/arrow-down-simple.png';
import leftarrow from '../assets/left-arrow-backup-2-svgrepo-com.svg';
import rightarrow from '../assets/right-arrow-backup-2-svgrepo-com.svg';
import { FaChevronDown } from "react-icons/fa";
import arrowdown from '../assets/arrow-down-simple.png';
import { fetchOrders } from '../services/orderService';
import { fetchProductByCategoryAndId } from '../services/productService';
import { normalizeCategory } from '../constants/agencyCategories';

const tabs = ["All Orders", "Shipped", "Completed", "Pending", "Cancelled"];

export default function Orders() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const selectedFromURL = queryParams.get("selected");

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [filterOptions, setFilterOptions] = useState({});
  const [activeTab, setActiveTab] = useState("All Orders");
  const [currentPage, setCurrentPage] = useState(1);
  const [isOpen, setIsOpen] = useState(false);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [openItems, setOpenItems] = useState({});

  const OrdersPerPage = 10;

async function getProductField(productId, category, field) {
  try {
    const normalized = normalizeCategory(category);
    const product = await fetchProductByCategoryAndId(normalized, productId);
    return product?.[field] ?? null;
  } catch (err) {
    console.error(`Error fetching product ${field}:`, err);
    return null;
  }
}

const fetchOrdersData = useCallback(async () => {
  setLoading(true);
  setError(null);

  try {
    const apiOrders = await fetchOrders();
    const orderList = Array.isArray(apiOrders) ? apiOrders : [];

    const normalized = await Promise.all(
      orderList.map(async (order) => {
        const productId = order?.Product?._id;
        const category = order?.Product?.Category;

        const vendorName = productId && category
          ? await getProductField(productId, category, "vendorName")
          : "-";

        const productName = productId && category
          ? await getProductField(productId, category, "name")
          : "-";

        const productPrice = productId && category
          ? await getProductField(productId, category, "rentalFee")
          : "-";

        return {
          id: order._id || order.id,
          status: order.status || "Pending",
          createdAt: order.orderDate || order.createdAt || new Date().toISOString(),
          returnDate: order.returnDate || null,
          total: order.totalAmount ?? order.total ?? 0,
          numOfDays: order.numOfDays ?? 0,
          customerName: order.Customer?.name || "Unknown",
          vendor: vendorName || "-",   
          method: order.method || "-",
          profit: order.profit ?? 0,
          
          shipping: order.shipping ?? 0,
          productName: productName || "-",
          productId: productId || null,
          productPrice: productPrice || "-",
         
          items: [
            {
              id: productId || "item1",
              name: productName,
              sku: "SKU-001",
              price: productPrice,
              discount: order.discount ? 0 : 0,
              rentalDate: order.orderDate || "2023-11-01",
              returnDate: order.returnDate || "2023-11-07"
            }
          ]
        };
      })
    );

    setOrders(normalized);

  } catch (err) {
    console.error("Error fetching orders:", err);
    setError("Failed to fetch orders. Please check the network.");
    setOrders([]);
  } finally {
    setLoading(false);
  }
}, []);

  

  useEffect(() => {
    fetchOrdersData();
  }, [fetchOrdersData]);

  useEffect(() => {
    if (selectedFromURL) {
      const matchedTab = tabs.find(tab => tab.toLowerCase().startsWith(selectedFromURL.toLowerCase()));
      if (matchedTab) setActiveTab(matchedTab);
    }
  }, [selectedFromURL]);

  // فلترة البيانات حسب التاب والبحث والفلتر
const getFilteredData = (tab) => {
  const searchLower = search.toLowerCase();

  return orders
    .filter(item => tab === "All Orders" ? true : item.status === tab)
    .filter(item => {
      return (
        String(item.customerName || "").toLowerCase().includes(searchLower) ||
        String(item.id || "").toLowerCase().includes(searchLower)
      );
    })
    .filter(item => {
      const currentFilter = filterOptions[tab];
      if (!currentFilter || currentFilter === "") return true;
      return item.status === currentFilter;
    });
};

// عند التبديل بين التابز
const handleTabClick = (tab) => {
  setActiveTab(tab);
  setFilterOptions(prev => ({ ...prev, [tab]: "" })); // مسح فلتر التاب القديم
  setCurrentPage(1);
  navigate(`/transactions?selected=${tab.split(" ")[0].toLowerCase()}`);
};


  const filteredOrders = getFilteredData(activeTab);
  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / OrdersPerPage));
  const indexOfLast = currentPage * OrdersPerPage;
  const indexOfFirst = indexOfLast - OrdersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirst, indexOfLast);

  const filtersByTab = {
    "All Orders": [{key: "Pending", label: "Pending"}, {key: "Completed", label: "Completed"}, {key: "Cancelled", label: "Cancelled"}, {key: "Shipped", label: "Shipped"}],
    "Pending": [{key: "Pending", label: "Pending"}],
    "Completed": [{key: "Completed", label: "Completed"}],
    "Cancelled": [{key: "Cancelled", label: "Cancelled"}],
    "Shipped": [{key: "Shipped", label: "Shipped"}]
  };

  const toggleExpand = (id) => {
    setExpandedOrder(prev => prev === id ? null : id);
  };

  const toggleOrder = (id) => {
    setOpenItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const exportToCSV = (tab) => {
    const filteredData = getFilteredData(tab);
    if (!filteredData.length) { alert("No orders to export!"); return; }

    const headers = Object.keys(filteredData[0]);
    const rows = filteredData.map(order => headers.map(h => {
      const v = order[h];
      if (v == null) return '';
      if (typeof v === 'object') return JSON.stringify(v);
      return String(v).replace(/\n/g, ' ');
    }).join(","));
    const csvContent = [headers.join(","), ...rows].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${tab.replace(/\s+/g, "_")}_orders.csv`;
    link.click();
  };

  const handleNewOrder = () => { navigate("/transactions/add", { state: { fromOrders: true, activeTab } }); };

  const handleStatusChange = (id, newStatus) => {
    setOrders(prev => prev.map(order => order.id === id ? {...order, status: newStatus} : order));
  };

  const toggleSelectAll = () => {
    if (currentOrders.length === 0) return setSelectedOrders([]);
    if (selectedOrders.length === currentOrders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(currentOrders.map(p => p.id));
    }
  };

  const toggleSelectOrder = (id) => {
    setSelectedOrders(prev => prev.includes(id) ? prev.filter(pid => pid !== id) : [...prev, id]);
  };

  if (loading) {
    return (
      <div className="md:p-10 p-4 pb-4 text-base gap-4 dark:bg-black min-h-screen flex justify-center items-center">
        <p className="text-xl dark:text-gray-200">Loading Orders...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="md:p-10 p-4 pb-4 text-base gap-4 dark:bg-black min-h-screen flex justify-center items-center">
        <p className="text-xl text-red-500">{error}</p>
        <button onClick={fetchOrdersData} className="ml-4 agarly-btn-primary">Try Again</button>
      </div>
    );
  }

  return (
    <div className="md:p-10 p-4 pb-4 text-base gap-4 dark:bg-black">
      <h1 className="text-2xl font-bold mb-2 dark:text-gray-200">Orders</h1>
      <p className="text-gray-600 pb-6 text-sm dark:text-gray-400">
        <Link to="/dashboard" className="hover:underline ">Dashboard</Link> / Orders
        {activeTab ? <span className="agarly-gradient-text font-bold"> / {activeTab}</span> : ""}
      </p>

      <div className="relative bg-white p-6 rounded-2xl shadow-md dark:bg-[#1A1A1B]">
        <div className="mb-6">
          <div className="flex flex-row items-center justify-between mb-4">
            <div className="hidden sm:block relative w-[40%]">
              <input
                type="text"
                placeholder="Search Orders"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                className="focus:outline-none focus:ring-1 focus:ring-gray-500 border-2 border-gray-400 rounded-xl pr-10 pl-3 py-2 text-l w-[100%] dark:bg-[#1A1A1B]"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2">
                <img src={searchIcon} alt="search" className="w-5 h-5" />
              </span>
            </div>

            <div className="flex justify-between gap-2 md:gap-3">
              <div className="relative">
                <button
                  onClick={() => setShowFilter(prev => !prev)}
                  className="flex border-2 border-gray-400 rounded-xl px-2 py-2 gap-2 text-sm items-center"
                >
                  <p className="dark:text-gray-200">Filter</p>
                  <img src={filter} alt="" className="w-5 h-5 md:w-6 md:h-6"/>
                </button>

                {showFilter && filtersByTab[activeTab]?.length > 0 && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-black dark:text-gray-200 border rounded-xl shadow-lg p-2 z-20">
                    {filtersByTab[activeTab].map(f => (
                      <button
                        key={f.key}
                        className="block w-full text-left px-2 py-1 hover:bg-gray-100 dark:hover:bg-[#2D2D2D]"
                        onClick={() => { setFilterOptions(prev => ({ ...prev, [activeTab]: f.key })); setShowFilter(false); setCurrentPage(1); }}
                      >
                        {f.label}
                      </button>
                    ))}
                    <button
                      className="block w-full text-left px-2 py-1 hover:bg-gray-100 dark:hover:bg-[#2D2D2D]"
                      onClick={() => { setFilterOptions(prev => ({ ...prev, [activeTab]: "" })); setShowFilter(false); setCurrentPage(1); }}
                    >
                      Clear Filter
                    </button>
                  </div>
                )}
              </div>

              <button onClick={() => exportToCSV(activeTab)} className="flex border-2 border-gray-400 rounded-xl px-2 py-2 gap-2 text-sm items-center">
                <p className="dark:text-gray-200">Export</p>
                <img src={download} alt="" className="w-5 h-5 md:w-6 md:h-6"/>
              </button>

              <button onClick={handleNewOrder} className="flex  rounded-xl px-2 py-2 gap-2 text-sm items-center agarly-btn-primary text-agarly-dark">
                <p className="">New Order</p>
                <img src={add} alt="" className="w-5 h-5 md:w-6 md:h-6"/>
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-4 mt-0">
            {filterOptions[activeTab] && (
              <div className="flex items-center gap-1 bg-blue-100 text-gray-600 px-2 py-1 rounded-full text-xs">
                {filtersByTab[activeTab].find(f => f.key === filterOptions[activeTab])?.label || filterOptions[activeTab]}
                <button onClick={() => setFilterOptions(prev => ({ ...prev, [activeTab]: "" }))} className="ml-1 font-bold">×</button>
              </div>
            )}
          </div>

          <div className="py-1 px-1 mb-4 border-2 border-gray-300 rounded-xl">
            <div className="grid grid-cols-3 gap-2 sm:flex sm:space-x-4 sm:justify-between">
              {tabs.map((tab, index) => (
                <button
                  key={tab}
                  className={`py-1 text-sm rounded-lg ${activeTab === tab ? "bg-blue-100 text-gray-600" : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-[#2D2D2D]"} ${index < 3 ? "col-span-1 md:w-[24%]" : "col-span-1 md:w-[24%]"}`}
                  onClick={() => handleTabClick(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-gray-300 dark:border-[#3D3D3D] shadow-md">
            <table className="min-w-full border-collapse bg-white dark:bg-[#1A1A1B] hidden sm:table md:table ">
              <thead className="bg-gray-100 border-b border-[#E7E7E7] dark:bg-black dark:border-[#3D3D3D] dark:text-gray-400">
              <tr>
                <th className="text-left px-4 py-4 text-black text-sm font-medium dark:text-gray-200">
                <div className="flex gap-4 items-center">
                  <CustomDarkCheckbox 
                  checked={selectedOrders.length === currentOrders.length && currentOrders.length > 0}
                  onChange={toggleSelectAll}/>
                  <span>Order ID</span>
                </div>
                </th>
                <th className="text-left px-4 py-3 text-black text-sm font-medium dark:text-gray-200">Created</th>
                <th className="text-left px-4 py-3 text-black text-sm font-medium dark:text-gray-200">Customer</th>
                <th className="text-left px-4 py-3 text-black text-sm font-medium dark:text-gray-200">Vendor</th>
                <th className="text-left px-4 py-3 text-black text-sm font-medium dark:text-gray-200">Total</th>
                <th className="text-left px-4 py-3 text-black text-sm font-medium dark:text-gray-200">Method</th>
                <th className="text-left px-4 py-3 text-black text-sm font-medium dark:text-gray-200">Profit</th>
                <th className="text-left px-4 py-3 text-black text-sm font-medium dark:text-gray-200">Status</th>
                <th className="text-left px-4 py-3 text-black text-sm font-medium dark:text-gray-200"></th>
              </tr>
              </thead>
              <tbody>
              {currentOrders.length === 0 ? (
                <tr>
                <td colSpan={9} className="text-center py-4 dark:text-gray-400">No orders found</td>
                </tr>
              ) : (
                currentOrders.map((order) => (
                <React.Fragment key={order.id}>
                  <tr className={`border-b border-gray-200 hover:bg-gray-50 dark:hover:bg-[#2D2D2D] dark:border-[#3D3D3D] ${expandedOrder === order.id ? "bg-gray-100 hover:bg-gray-100 dark:bg-[#2D2D2D]" : ""}`}>
                  <td className="px-4 py-3 flex gap-4 items-center text-gray-800 dark:text-gray-200">
                    <CustomDarkCheckbox 
                    checked={selectedOrders.includes(order.id)}
                    onChange={() => toggleSelectOrder(order.id)}/>

                    <p className="ml-2 text-sm agarly-gradient-text">{order.id}</p>
                  </td>

                  <td className="px-4 py-3 text-gray-700 text-sm dark:text-gray-300">
                    {new Date(order.createdAt).toLocaleDateString("en-GB")} <br />
                    {new Date(order.createdAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: true })}
                  </td>
                  <td className="px-4 py-3 text-gray-700 text-sm dark:text-gray-300">{order.customerName}</td>
                  <td className="px-4 py-3 text-gray-700 text-sm dark:text-gray-300">{order.vendor}</td>
                  <td className="px-4 py-3 text-gray-700 text-sm dark:text-gray-300">${Number(order.total).toLocaleString()}</td>
                  <td className="px-4 py-3 text-gray-700 text-sm dark:text-gray-300">{order.method}</td>
                  <td className="px-4 py-3 text-gray-700 text-sm dark:text-gray-300">${Number(order.profit).toLocaleString()} 
                    <span className="bg-green-100 py-1 px-2 rounded-xl ml-2 text-green-700 text-xs font-medium">
                      {order.total && Number(order.total) !== 0 ? ((Number(order.profit)/Number(order.total)) * 100).toFixed(0) : 0}%
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                  <select
                    value={order.status}
                    onChange={(e) => handleStatusChange(order.id, e.target.value)}
                    className={`px-3 py-2 rounded-md text-xs font-semibold cursor-pointer appearance-none transition-colors duration-300 ${order.status === "Pending" ? "bg-agarly-warning/20 text-amber-700 focus:outline-none focus:ring-1 focus:ring-agarly-warning" : order.status === "Shipped" ? "bg-agarly-secondary/30 text-agarly-dark focus:outline-none focus:ring-1 focus:ring-agarly-secondary" : order.status === "Completed" ? "bg-green-100 text-green-700 focus:outline-none focus:ring-1 focus:ring-green-300" : order.status === "Cancelled" ? "bg-red-100 text-red-700 focus:outline-none focus:ring-1 focus:ring-red-300" : "bg-gray-100 text-gray-700 focus:outline-none focus:ring-1 focus:ring-gray-300"}`
                    }>
                    <option value="Pending">Pending</option>
                    <option value="Shipped">Shipped</option>
                    <option value="Completed">Completed</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                  </td>


                  <td className="px-4 py-3 text-gray-700 text-sm dark:text-gray-300">
                    <button onClick={() => toggleExpand(order.id)} aria-label="Toggle Order Details">
                    <img src={arrow} alt="toggle" className={`w-5 h-5 transition-transform ${expandedOrder === order.id ? "rotate-180" : ""} dark:invert`} />
                    </button>
                  </td>
                  </tr>

                  {expandedOrder === order.id && (
                  <tr className="bg-gray-50 dark:bg-black border-b">
                    <td colSpan={9} className="px-6 py-4">
                    {(
                      <>
                      <table className="w-full text-sm">
                        <thead className="dark:bg-black text-gray-500 dark:text-gray-200">
                        <tr>
                          <th className="p-2 py-4 text-left">#</th>
                          <th className="p-2 py-4 text-left">NAME</th>
                          <th className="p-2 py-4 text-left">PRICE</th>
                          <th className="p-2 py-4 text-left">RENTAL DATE</th>
                          <th className="p-2 py-4 text-left">RETURN DATE</th>
                          <th className="p-2 py-4 text-left">DISC.</th>
                          <th className="p-2 py-4 text-left">TOTAL</th>
                          <th className="p-2 py-4 text-left">🖨️ PRINT</th>
                        </tr>
                        </thead>
                        <tbody>
                        {order.items.map((it, idx) => {
                          const price = Number(it.price ?? 0);
                          const discount = Number(it.discount ?? 0);
                          const discountValue = (price * discount) / 100;
                          const finalPrice = price - discountValue;

                          return (
                          <tr key={idx} className="border-t py-4 dark:border-[#3D3D3D]">
                            <td className="p-2 py-4 dark:text-gray-300">#{order.productId ?? idx+1}</td>
                            <td className="p-2 dark:text-gray-300 font-semibold">{it.name ?? "-"}</td>
                            <td className="p-2 dark:text-gray-300">${price.toFixed(2)}</td>
                            <td className="p-2 dark:text-gray-300">
                              {new Date(it.rentalDate).toLocaleDateString("en-GB")} <br />
                              {new Date(it.rentalDate).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: true })}</td>
                            <td className="p-2 dark:text-gray-300">
                              {new Date(it.returnDate).toLocaleDateString("en-GB")} <br />
                              {new Date(it.returnDate).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: true })}
                            </td>
                            <td className="p-2 text-red-500 dark:text-red-500">{discount}%</td>
                            <td className="p-2 dark:text-gray-300">${finalPrice.toFixed(2)}</td>
                            <td className="p-2 text-gray-700 text-lg cursor-pointer font-bold dark:text-gray-300">...</td>
                          </tr>
                          );
                        })}
                        </tbody>
                      </table>

                      {(() => {
                        const subtotal = order.items.reduce((acc, it) => {
                        const price = Number(it.price ?? 0);
                        const discount = Number(it.discount ?? 0);
                        const discountValue = (price * discount) / 100;
                        const numOfDays = Number(order.numOfDays ?? 0);

                        const finalPrice = (price - discountValue) * numOfDays;

                        return acc + finalPrice;
                      }, 0);


                        const totalDiscount = order.items.reduce((acc, it) => acc + ((Number(it.price ?? 0) * Number(it.discount ?? 0)) / 100), 0);

                        const shipping = Number(order.shipping ?? 0);
                        const finalTotal = subtotal + shipping;

                        return (
                        <div className="flex justify-end mt-4">
                          <table className="">
                          <tbody>
                            <tr>
                              <td className="px-20 py-1 text-gray-600 dark:text-gray-300">Subtotal</td>
                              <td className="px-4 py-1 text-gray-800 dark:text-gray-200">$ {subtotal.toFixed(2)}</td>
                            </tr>
                            <tr>
                              <td className="px-20 py-1 text-gray-600 dark:text-gray-300">Shipping</td>
                              <td className="px-4 py-1 text-gray-800 dark:text-gray-200">$ {shipping.toFixed(2)}</td>
                            </tr>
                            <tr>
                              <td className="px-20 py-1 text-gray-600 dark:text-gray-300">Discount</td>
                              <td className="px-4 py-1 text-red-500">$ {totalDiscount.toFixed(2)}</td>
                            </tr>
                            <tr>
                              <td className="px-20 py-1 font-semibold text-gray-800 dark:text-gray-200">Total</td>
                              <td className="px-4 py-1 font-semibold text-gray-800 dark:text-gray-200">$ {finalTotal.toFixed(2)}</td>
                            </tr>
                          </tbody>
                          </table>
                        </div>
                        );
                      })()}
                      </>
                    ) }
                    </td>
                  </tr>
                  )}

                </React.Fragment>
                ))
              )}
              </tbody>
            </table>
          </div>

            <div className="sm:hidden flex flex-col items-start gap-6 w-full mt-6">
            {currentOrders.length === 0 ? (
                <div className="w-full text-center py-4 dark:text-gray-400 bg-white dark:bg-[#1A1A1B] rounded-xl shadow-md">No orders found</div>
            ) : (
            currentOrders.map((order) => (
                <div key={order.id} className="relative w-full border border-gray-200 dark:border-gray-700 rounded-xl shadow-md bg-white dark:bg-[#1A1A1B]">
                <div className="flex justify-between items-center w-full py-3 px-4">
                    <div className="flex flex-col">
                    <p className="text-xs agarly-gradient-text font-semibold">Order #{order.id}</p>
                    <p className="dark:text-gray-300 font-medium">{order.customerName}</p>
                    <select
                      value={order.status}
                      onChange={(e) => handleStatusChange(order.id, e.target.value)}
                      className={`mt-1 px-2 py-1 rounded-md text-xs font-semibold cursor-pointer appearance-none ${order.status === "Pending" ? "bg-agarly-warning/20 text-amber-700" : order.status === "Shipped" ? "bg-agarly-secondary/30 text-agarly-dark" : order.status === "Completed" ? "bg-green-100 text-green-700" : order.status === "Cancelled" ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-700"}`}
                    >
                      <option value="Pending">Pending</option>
                      <option value="Shipped">Shipped</option>
                      <option value="Completed">Completed</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                    </div>

                    <div className="flex flex-col items-end">
                      <p className="text-lg font-bold dark:text-gray-200">$ {Number(order.total).toLocaleString()}</p>
                      <button onClick={() => toggleOrder(order.id)} className="mt-1 p-1">
                      <img src={arrowdown} alt="toggle" className={`w-5 h-5 transition-transform dark:invert ${openItems[order.id] ? "rotate-180" : ""}`} />
                      </button>
                    </div>
                </div>

                {openItems[order.id] && (
                    <div className="bg-gray-50 w-full rounded-b-xl p-4 border-t border-gray-200 dark:bg-black dark:border-gray-700">
                    <div className="text-gray-900 text-sm mb-2 flex justify-between">
                        <span className="text-gray-500 dark:text-gray-300 font-medium">Date:</span>
                        <span className="dark:text-gray-400 text-right">{new Date(order.createdAt).toLocaleDateString("en-GB")} - {new Date(order.createdAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: true })}</span>
                    </div>
                    <div className="text-gray-900 text-sm mb-2 flex justify-between">
                        <span className="text-gray-500 dark:text-gray-300 font-medium">Vendor:</span>
                        <span className="dark:text-gray-400">{order.vendor}</span>
                    </div>
                    <div className="text-gray-900 text-sm mb-2 flex justify-between">
                        <span className="text-gray-500 dark:text-gray-300 font-medium">Method:</span>
                        <span className="dark:text-gray-400">{order.method}</span>
                    </div>
                    <div className="text-gray-900 text-sm mb-2 flex justify-between">
                        <span className="text-gray-500 dark:text-gray-300 font-medium">Profit:</span>
                        <span className="dark:text-gray-400">$ {order.profit} <span className="bg-green-100 py-1 px-2 rounded-xl ml-2 text-green-700 text-xs font-medium">{order.total && Number(order.total) !== 0 ? ((Number(order.profit)/Number(order.total)) * 100).toFixed(0) : 0}%</span></span>
                    </div>
                    <div className="text-gray-900 text-sm mb-2 flex justify-between">
                        <span className="text-gray-500 dark:text-gray-300 font-medium">Items:</span>
                        <span className="dark:text-gray-400">{order.items?.length || 0} items</span>
                    </div>
                    </div>
                )}
                </div>
            )))
          }
            
            </div>

        <div className="flex justify-between items-center mt-4">
          <p className="text-sm text-gray-700 dark:text-gray-400">Page {currentPage} of {totalPages}</p>
          <div className="flex gap-2 items-center">
            <button className="border-2 px-1 py-1 rounded-xl disabled:opacity-50 dark:border-gray-600" disabled={currentPage===1} onClick={()=>setCurrentPage(p=>Math.max(1,p-1))} aria-label="Previous Page"><img src={leftarrow} alt="" className="w-5 h-5 dark:invert"/></button>
            <div className="relative inline-block">
              <button onClick={()=>setIsOpen(prev=>!prev)} className="flex items-center gap-1 border px-2 py-1 rounded-xl dark:text-gray-200 dark:border-gray-600">{currentPage} <FaChevronDown /></button>
              {isOpen && 
                <div className="absolute right-0 bottom-full bg-white border rounded-md shadow z-10 mb-2 dark:bg-black dark:border-gray-600 dark:text-gray-200 max-h-40 overflow-y-auto">
                {Array.from({length:totalPages},(_,i)=>i+1).map(p=>(
                  <div key={p} onClick={()=>{setCurrentPage(p); setIsOpen(false)}} className={`px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-[#2D2D2D] ${p===currentPage?"bg-gray-200 font-semibold dark:bg-[#2D2D2D]":""}`}>{p}</div>
                ))}
                </div>
              }
            </div>
            <button className="border-2 px-1 py-1 rounded-xl disabled:opacity-50 dark:border-gray-600" disabled={currentPage===totalPages} onClick={()=>setCurrentPage(p=>Math.min(totalPages,p+1))} aria-label="Next Page"><img src={rightarrow} alt="" className="w-5 h-5 dark:invert"/></button>
          </div>
        </div>

          </div>
        </div>
    </div>
  );
}

